import { TranslateFn } from "../../utils/types";

/**
 * Statut d'un sinistre, tel que stocke par l'API.
 *
 * POURQUOI CE N'EST PLUS UN ENUM
 * ------------------------------
 * La version precedente etait :
 *
 *   export enum SinisterStatus {
 *     "En cours" = "B",
 *     "Ferme"    = "C",
 *     "Declare"  = "D",
 *   }
 *
 * et `getSinisterKeyLabelByValue("B")` renvoyait la CLE de l'enum - le
 * litteral "En cours" - affiche tel quel dans le chip de statut. La copie
 * d'affichage etait stockee comme identifiant TypeScript : aucun passage par
 * i18next sur ce chemin, donc "EN COURS" restait en francais quelle que soit
 * la langue choisie.
 *
 * C'est le jumeau exact de VehicleStatus dans utils/types.ts, corrige de la
 * meme facon : seuls les codes circulent, les libelles sont resolus au RENDER
 * par i18next, ce qui les fait suivre un changement de langue a chaud.
 */
export type SinisterStatusCode = "B" | "C" | "D";

export const SINISTER_STATUS = {
  IN_PROGRESS: "B",
  CLOSED: "C",
  DECLARED: "D",
} as const satisfies Record<string, SinisterStatusCode>;

/** Ordre d'affichage du <select> et de la legende. */
export const SINISTER_STATUS_CODES: SinisterStatusCode[] = ["B", "C", "D"];

/**
 * Code -> cle i18n. Le fallback est la chaine ANGLAISE, jamais la francaise :
 * un fallback est ce qui s'affiche quand la cle manque d'une locale, donc un
 * fallback francais reintroduit silencieusement le bug qu'on vient de retirer.
 */
export const SINISTER_STATUS_LABELS: Record<
  SinisterStatusCode,
  { key: string; fallback: string }
> = {
  B: { key: "claims.statusInProgress", fallback: "In progress" },
  C: { key: "claims.statusClosed", fallback: "Closed" },
  D: { key: "claims.statusDeclared", fallback: "Declared" },
};

export const isSinisterStatusCode = (
  value: unknown
): value is SinisterStatusCode =>
  typeof value === "string" && value in SINISTER_STATUS_LABELS;

/**
 * Traduit un code de statut. Passer le `t` de useTranslation() - PAS le
 * singleton i18n - pour que le libelle se recalcule au changement de langue.
 */
export const translateSinisterStatus = (
  t: TranslateFn,
  code: unknown
): string => {
  if (!isSinisterStatusCode(code)) return "";
  const entry = SINISTER_STATUS_LABELS[code];
  return t(entry.key, entry.fallback);
};

/** Options pour CustomTextSelect : libelle traduit, valeur = code. */
export const getSinisterStatusOptions = (
  t: TranslateFn
): { label: string; value: SinisterStatusCode }[] =>
  SINISTER_STATUS_CODES.map((code) => ({
    label: translateSinisterStatus(t, code),
    value: code,
  }));
